import * as action from '../constants/projectDetail';
import cloneDeep from 'lodash/cloneDeep';
import { FirstLoadProjectStages } from 'constants/project-detail';
import { ShowDangerAlert, ShowSuccessAlert } from 'utils/toast';

const initialState = {
  isFetchingProjectDetail: false,
  project: {},
  error: '',
  searchedText: '',
  selectedItems: [],
  isFirstTimeLoad: false,
  executionList: [],
  errorGettingExecutionList: '',
  discoveryJobId: '',
  discoveryError: '',
  executingProject: false,
  executingProjectSuccess: false,
  executingProjectFailure: false,
  stages: cloneDeep(FirstLoadProjectStages),
  nextCursor: '',
  isFetchingMore: false,
  isFetchingExecutionList: false,
  fileToDeleteTempCopy: null,
};

function projectDetailReducer(state = initialState, actions) {
  const { type, payload, extra } = actions;
  const oldState = cloneDeep(state);
  switch (type) {
    case action.HYP_UPDATE_DETAIL_FETCH_STATUS:
      return {
        ...oldState,
        isFetchingProjectDetail: payload,
      };
    case action.HYP_UPDATE_PROJECT_STAGE:
      const { stage, status } = payload;
      const oldStages = oldState.stages;
      oldStages[stage].status = status;
      return {
        ...oldState,
        stages: oldStages,
      };
    case action.HYP_UPDATE_DETAIL_INFO:
      if (
        !payload ||
        payload?.error ||
        (payload?.response?.status !== 200 && payload?.status !== 'success')
      ) {
        return { ...oldState, error: payload };
      }
      return {
        ...oldState,
        project: {
          artifacts: payload?.data?.artifacts,
          projectName: payload?.data?.name,
          framework: payload?.data?.type,
          username: payload?.data?.workspace?.user,
          license: payload?.data?.license,
          workspace: payload?.data?.workspace,
          db: payload?.data?.db,
          projectId: payload?.data?.id,
          dexAddress: payload?.data?.dexAddress,
          projectRootName: payload?.data?.projectRootName,
          createdBy: payload?.data?.created_by,
          createdAt: payload?.data?.created_at,
          language: payload?.data?.language,
          yamlPath: payload?.data?.yaml_path,
          branch: payload?.data?.branch_name,
          repo_url: payload?.data?.repo_url,
        },
        isFirstTimeLoad: false,
        isFetchingProjectDetail: false,
        discoveryJobId: '',
        discoveryError: '',
        stages: cloneDeep(FirstLoadProjectStages),
      };
    case action.HYP_UPDATE_PROJECT_NAME_FRAMEWORK_WORKSPACE:
      return {
        ...oldState,
        project: {
          ...oldState.project,
          projectName: payload.projectName,
          framework: payload.framework,
          username: payload.username,
        },
      };
    case action.HYP_UPDATE_DETAIL_ERROR:
      return {
        ...oldState,
        error: payload,
      };
    case action.HYP_UPDATE_PROJECT_EXECUTION_STATUS:
      return {
        ...oldState,
        executingProject: payload,
        executingProjectFailure: false,
      };
    case action.HYP_UPDATE_PROJECT_EXECUTION_TRIGGER_STATUS:
      const isSuccess = ![400, 403, 501, 503].includes(
        payload.response?.status
      );
      return {
        ...oldState,
        executingProjectSuccess: isSuccess,
        executingProjectFailure: !isSuccess,
        executingProject: true,
      };
    case action.HYP_UPDATE_PROJECT_EXECUTION_SUCCESS:
      return {
        ...oldState,
        executingProjectSuccess: payload,
        executingProjectFailure: payload,
        executingProject: false,
      };

    case action.HYP_UPDATE_DETAIL_SEARCH_TEXT:
      return {
        ...oldState,
        searchedText: payload,
      };

    case action.HYP_UPDATE_FIRST_TIME_LOAD_STATUS:
      return {
        ...oldState,
        isFirstTimeLoad: payload,
      };
    case action.HYP_UPDATE_DETAIL_ITEM_SELECTION:
      return {
        ...oldState,
        selectedItems: payload,
      };
    case action.HYP_UPDATE_JOB_ID_FOR_DISCOVERED_PROJECT:
      if (!payload || payload?.error || !payload?.jobId) {
        return { ...oldState, discoveryError: payload };
      }
      return {
        ...oldState,
        discoveryJobId: payload?.jobId || '',
      };
    case action.HYP_RESET_PROJECT_DETAIL:
      return {
        ...initialState,
      };
    case action.HYP_EXECUTION_LIST_INFINITE_SCROLL_LOADING:
      return {
        ...oldState,
        isFetchingMore: true,
      };
    case action.HYP_UPDATE_EXECUTION_LIST_FETCH_STATUS:
      return {
        ...oldState,
        isFetchingExecutionList: payload,
      };
    case action.HYP_DELETE_FILE_FROM_STORE: {
      const executionList = oldState.executionList.filter(
        (e) => e.id !== payload?.data?.id
      );
      return {
        ...state,
        executionList,
        fileToDeleteTempCopy: payload,
        selectedItems: oldState.selectedItems.filter((f) => {
          return f !== payload?.data?.id;
        }),
      };
    }

    case action.HYP_DELETE_FILE_FROM_SERVER: {
      const fileName = oldState?.fileToDeleteTempCopy?.data?.name;
      let fileToDeleteTempCopy = oldState?.fileToDeleteTempCopy;
      let executionList = oldState.executionList;

      if (payload?.response?.data?.error) {
        ShowDangerAlert(`Failed to delete file ${fileName}`);
        executionList.splice(
          fileToDeleteTempCopy?.index,
          0,
          fileToDeleteTempCopy?.data
        );
      } else {
        ShowSuccessAlert(`File ${fileName} Deleted Successfully.`);
        fileToDeleteTempCopy = null;
      }
      return {
        ...state,
        fileToDeleteTempCopy,
        executionList,
      };
    }

    case action.HYP_UPDATE_DETAIL_EXECUTION_LIST:
      if (!payload || payload?.error || !payload.data) {
        return {
          ...oldState,
          errorGettingExecutionList: payload,
          ...(extra?.clearExistingItems
            ? { executionList: [], selectedItems: [] }
            : {}),
          ...(!['cancelled', 'canceled'].includes(payload?.message)
            ? { isFetchingExecutionList: false }
            : {}),
        };
      }
      let counter = oldState.executionList.length;

      let newItems = [
        ...(payload?.data?.map((d = '', index) => {
          if (extra?.isJMeter) {
            return {
              id: d.id,
              order: extra?.clearExistingItems ? index + 1 : ++counter,
              name: d.name,
              path: d?.link,
            };
          }

          return {
            id: d.result,
            order: extra?.clearExistingItems ? index + 1 : ++counter,
            name: d.result.slice(d.result.lastIndexOf('/') + 1, d.length),
            path: d.result.slice(0, d.result.lastIndexOf('/')),
          };
        }) || []),
      ];

      let executionList = [...oldState.executionList, ...newItems];

      if (extra?.clearExistingItems) {
        executionList = [...newItems];
      }

      let filteredSelectedItems = oldState.selectedItems.filter((itemId) => {
        let idExists = false;

        idExists = executionList.some((d) => d.id === itemId);

        return idExists;
      });

      filteredSelectedItems = filteredSelectedItems.filter(
        (value, index, self) =>
          index === self.findIndex((t) => t.id === value.id)
      );

      executionList = executionList.filter(
        (value, index, self) =>
          index === self.findIndex((t) => t.id === value.id)
      );

      return {
        ...oldState,
        nextCursor: payload?.metadata?.next_cursor,
        isFetchingMore: false,
        isFetchingExecutionList: false,
        executionList,
        selectedItems: filteredSelectedItems,
      };
    default:
      return state;
  }
}

export default projectDetailReducer;
